import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import type { Account } from '@/common/types'
import { APP_ROUTES } from '@/common/constants'

type Args = {
    isAccountSwitching: boolean
    activeAccountId: string | null
    failedAccountIds: Set<string>
    resolvedPrimaryAccount: Account | null
    switchAccount: (newId: string | null, options?: { refreshMetadata?: boolean }) => Promise<boolean>
}

const getKycConfirmTargetAccountId = (state: unknown): string | null => {
    if (!state || typeof state !== 'object') return null
    const candidate = state as { createdAccountId?: unknown }
    return typeof candidate.createdAccountId === 'string' ? candidate.createdAccountId : null
}

const getRouteTargetAccountId = ({
    pathname,
    state,
    resolvedPrimaryAccount,
}: {
    pathname: string
    state: unknown
    resolvedPrimaryAccount: Account | null
}): string | null => {
    if (pathname === APP_ROUTES.APPLICATION_CREATE) {
        return resolvedPrimaryAccount?.accountId ?? null
    }

    if (pathname === APP_ROUTES.APPLICATION_KYC_CONFIRM) {
        return getKycConfirmTargetAccountId(state)
    }

    return null
}

export default function useRouteAccountEnforcement({
    isAccountSwitching,
    activeAccountId,
    failedAccountIds,
    resolvedPrimaryAccount,
    switchAccount,
}: Args) {
    const location = useLocation()
    const targetAccountId = useMemo(
        () =>
            getRouteTargetAccountId({
                pathname: location.pathname,
                state: location.state,
                resolvedPrimaryAccount,
            }),
        [location.pathname, location.state, resolvedPrimaryAccount],
    )

    useEffect(() => {
        if (!targetAccountId) return
        if (isAccountSwitching) return
        if (activeAccountId === targetAccountId) return
        if (failedAccountIds.has(targetAccountId)) return

        // KYC-confirm targets come from location.state and aren't guaranteed to be in the
        // fetched account data yet, so they need a metadata refresh to resolve. The
        // create-flow target is derived from resolvedPrimaryAccount, which is already
        // backed by fetched data, so it can safely skip the refresh.
        void switchAccount(targetAccountId, {
            refreshMetadata: location.pathname !== APP_ROUTES.APPLICATION_CREATE,
        })
    }, [targetAccountId, location.pathname, isAccountSwitching, activeAccountId, failedAccountIds, switchAccount])
}
